import React, { useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import type { ISelectedFolder } from '@/store/types';

import { useAppDispatch, useAppSelector, useResourceNavigation } from '@/hooks';
import {
  selectFoldersList,
  selectLatestDatabaseItems,
  selectPendingAction,
} from '@/store/selectors/resourcesSelectors';
import { LATEST_ITEMS_DAYS_BACK_THRESHOLD } from '@/constants';
import { ResourcesClientSection } from '@/components/common/resources';
import { getLatestDatabaseItems, getTree } from '@/store/slices/resourcesSlice';
import { combineLatestClientResources } from '@/store/combiners/resourcesCombiners';
import { LoaderWrapper } from '../loaders';
import { ResourcesClientHomeSkeleton } from './ResourcesClientHome.skeleton';

const resourcesClientHomeSelector = createSelector(
  selectFoldersList,
  selectLatestDatabaseItems,
  selectPendingAction(getTree.typePrefix),
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (foldersList, latestDatabaseItems, isTreePending, selectedFolder) => ({
    latestClientResources: combineLatestClientResources(foldersList, selectedFolder, latestDatabaseItems),
    isReady: !isTreePending && foldersList !== null,
  }),
);

const ResourcesClientHome = (): React.ReactElement => {
  const { selectedFolder } = useResourceNavigation();
  const { latestClientResources, isReady } = useAppSelector(state =>
    resourcesClientHomeSelector(state, selectedFolder),
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    const latestItemsCall = dispatch(getLatestDatabaseItems({ daysBack: LATEST_ITEMS_DAYS_BACK_THRESHOLD }));

    return () => latestItemsCall.abort();
  }, [dispatch]);

  return (
    <LoaderWrapper isLoading={!isReady} loader={<ResourcesClientHomeSkeleton />}>
      {latestClientResources ? <ResourcesClientSection clientResources={latestClientResources} /> : <></>}
    </LoaderWrapper>
  );
};

export { ResourcesClientHome };
